"use client";

import * as React from "react";
import { Eye, EyeOff, Lock } from "lucide-react";
import { Button, Input } from "@gerama/ui";
import { env } from "@/env";
import { GoogleSignInButton } from "./google-sign-in-button";
import { AuthLogo, FormError, OrDivider, maskMobile } from "./auth-bits";

export function AuthPasswordForm({
  mobile,
  onSubmit,
  onGetCodeBySms,
  onForgotPassword,
  onBack,
  isLoading,
  error,
}: {
  mobile: string;
  onSubmit: (password: string) => void;
  onGetCodeBySms: () => void;
  onForgotPassword: () => void;
  onBack: () => void;
  isLoading: boolean;
  error?: string;
}) {
  const [show, setShow] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const masked = maskMobile(mobile);

  React.useEffect(() => {
    if (isLoading) return;
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [isLoading, error]);

  return (
    <form
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        const password = String(new FormData(event.currentTarget).get("password") ?? "").trim();
        if (password) onSubmit(password);
      }}
    >
      <AuthLogo />
      <p className="text-muted-foreground mt-5 mb-4 text-sm">
        شما با شماره{" "}
        <button
          type="button"
          onClick={onBack}
          disabled={isLoading}
          dir="ltr"
          className="text-foreground underline-offset-2 hover:underline disabled:opacity-50"
        >
          {masked}
        </button>{" "}
        در حال ورود هستید.
      </p>
      <p className="text-muted-foreground mb-4 text-sm">رمز عبور خود را وارد کنید.</p>
      <div className="relative mb-4">
        <Lock className="text-muted-foreground absolute top-1/2 start-2.5 size-4 -translate-y-1/2" />
        <Input
          ref={inputRef}
          name="password"
          type={show ? "text" : "password"}
          autoComplete="current-password"
          placeholder="رمز عبور"
          aria-label="رمز عبور"
          disabled={isLoading}
          className="h-11 rounded-lg bg-muted/50 ps-9 pe-10"
        />
        <button
          type="button"
          onClick={() => setShow((value) => !value)}
          className="text-muted-foreground hover:text-foreground absolute top-1/2 end-2.5 -translate-y-1/2 rounded p-1"
          aria-label={show ? "پنهان کردن رمز" : "نمایش رمز"}
        >
          {show ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </button>
      </div>
      <FormError message={error} />
      <Button type="submit" className="mb-3 h-11 w-full rounded-lg" disabled={isLoading}>
        {isLoading ? "..." : "ادامه"}
      </Button>
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <Button
          type="button"
          variant="outline"
          className="h-11 flex-1 font-light"
          disabled={isLoading}
          onClick={onForgotPassword}
        >
          رمز عبور را فراموش کردید؟
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-11 flex-1 font-light"
          disabled={isLoading}
          onClick={onGetCodeBySms}
        >
          دریافت کد ورود با پیامک
        </Button>
      </div>
      {env.NEXT_PUBLIC_GOOGLE_CLIENT_ID ? (
        <>
          <OrDivider />
          <GoogleSignInButton disabled={isLoading} />
        </>
      ) : null}
      <Button type="button" variant="ghost" className="mt-4 h-11 w-full font-light" disabled={isLoading} onClick={onBack}>
        بازگشت
      </Button>
    </form>
  );
}
